import React, { useState, useEffect } from 'react';
import { slotRange, timeToMinutes } from './timeUtils';
import { resolveSlotAsset, resolveSlotLabel } from './labelResolver';
import { hashColor } from './color';
import AggregationChip from './AggregationChip';

export const TRACK_HEIGHT = 44;
export const TRACK_GAP = 6;
const MIN_RENDER_PX = 8;
// Multi-day (Week/Month) view: each day-pulse renders at this fraction
// of its actual time-range, centered. 0.55 = 55% pulse + ~22.5% whitespace
// on each side — gives a clear pill-in-column aesthetic.
const PILL_FRACTION = 0.55;

/**
 * Single timeline row (one screen).
 *
 * Walks slots in time order and merges any slot whose rendered width would be
 * below 8 px into an AggregationChip. The pixel-width threshold is applied
 * against `viewportWidth` (live measurement from the parent); positioning is
 * percentage-based so the layout itself doesn't need that measurement.
 */
const TimelineTrack = ({
  track,
  window: win,
  viewportWidth,
  contentMap,
  nowMin,
  nowMs,
  selectedContentId,
  onSelectContent,
  onHoverSlot,
  onLeaveSlot,
  onOpenLoopDrawer,
  openSlotId,
  // When set, clicking a slot calls this with the slot's intent id and the
  // slot's DOM gets data-clip-id for the floating InspectorBar to anchor to.
  // Used in multi-day (week/month) mode; falls back to the loop-drawer flow
  // in day mode where this prop is not supplied.
  onSelectIntent,
  selectedIntentId,
  // Week/Month view passes this true so sub-8px pulses render at a small
  // min width instead of collapsing into "···N" density chips (chips hid
  // narrower intents like Privacy Creative behind a black badge).
  suppressAggregation = false,
}) => {
  const { slots, isOffline } = track;
  const [hoveredSlotId, setHoveredSlotId] = useState(null);

  // Parent's nowMs ticks every 30s which lags 10s-per-item loops. Re-render
  // locally every 2s while a looping slot is active so the current-item
  // highlight keeps up with what's actually on screen.
  const [loopTick, setLoopTick] = useState(0);
  const hasAnyActiveLoop = slots.some((s) => {
    const st = timeToMinutes(s.startTime);
    const et = timeToMinutes(s.endTime);
    return nowMin >= st && nowMin < et
      && s.loopEnabled && Array.isArray(s.loopItems) && s.loopItems.length > 1;
  });
  useEffect(() => {
    if (!hasAnyActiveLoop) return undefined;
    const id = setInterval(() => setLoopTick((n) => n + 1), 2000);
    return () => clearInterval(id);
  }, [hasAnyActiveLoop]);
  // loopTick is consumed only to invalidate the render; actual time read
  // fresh from Date.now() on each re-render.
  void loopTick;
  const effectiveNowMs = hasAnyActiveLoop ? Date.now() : nowMs;

  // Prefer absolute-window minutes (`_startMinAbs`/`_endMinAbs`) when set —
  // day-range expansions add them so a single intent can render as one bar
  // per active day across a week/month X axis. Day view doesn't set these
  // and falls back to minutes-of-day parsed from the slot's wall-clock time.
  const slotStartMin = (s) => s._startMinAbs != null ? s._startMinAbs : timeToMinutes(s.startTime);
  const slotEndMin = (s) => s._endMinAbs != null ? s._endMinAbs : timeToMinutes(s.endTime);

  const measured = slots
    .slice()
    .sort((a, b) => slotStartMin(a) - slotStartMin(b))
    .map((slot) => {
      const startMin = slotStartMin(slot);
      const endMin = slotEndMin(slot);
      const range = slotRange(startMin, endMin, win);
      const px = range.widthPct * viewportWidth;
      return { slot, startMin, endMin, ...range, px };
    })
    .filter((m) => m.widthPct > 0);

  // Group runs of sub-8px slots into aggregation chips.
  const drawables = [];
  let bucket = null;
  const flush = () => {
    if (!bucket) return;
    if (bucket.slots.length === 1) {
      drawables.push({ kind: 'block', ...bucket.slots[0] });
    } else {
      drawables.push({
        kind: 'chip',
        slots: bucket.slots,
        leftPct: bucket.leftPct,
        widthPct: bucket.rightPct - bucket.leftPct,
      });
    }
    bucket = null;
  };

  // Greedy sub-lane assignment for multi-day pulses: overlapping same-day
  // intents (e.g. Rarity 00:00–21:00 + Privacy 23:02–23:59) each get their
  // own horizontal band so they don't occlude each other. Day view keeps a
  // single lane because slot merging upstream already guarantees no overlap.
  const subLaneEnds = [];
  const assignSubLane = (startMin, endMin) => {
    for (let i = 0; i < subLaneEnds.length; i++) {
      if (subLaneEnds[i] <= startMin) {
        subLaneEnds[i] = endMin;
        return i;
      }
    }
    subLaneEnds.push(endMin);
    return subLaneEnds.length - 1;
  };

  for (const m of measured) {
    // Week/Month: never aggregate — every intent stays visible as its own
    // colored pulse, enforced min-width applied at render time below.
    if (suppressAggregation) {
      const laneIdx = assignSubLane(m.startMin, m.endMin);
      drawables.push({ kind: 'block', subLane: laneIdx, ...m });
      continue;
    }
    if (m.px < MIN_RENDER_PX) {
      const right = m.leftPct + m.widthPct;
      if (!bucket) {
        bucket = { slots: [m], leftPct: m.leftPct, rightPct: right };
      } else {
        const gapPx = (m.leftPct - bucket.rightPct) * viewportWidth;
        if (gapPx > 12) {
          flush();
          bucket = { slots: [m], leftPct: m.leftPct, rightPct: right };
        } else {
          bucket.slots.push(m);
          bucket.rightPct = right;
        }
      }
    } else {
      flush();
      drawables.push({ kind: 'block', ...m });
    }
  }
  flush();

  // When multiple sub-lanes are stacked, grow the track so each lane is
  // at least MIN_LANE_H px tall — reliably hoverable and clickable.
  const MIN_LANE_H = 8;
  const multiLane = suppressAggregation && subLaneEnds.length > 1;
  const laneCount = subLaneEnds.length;
  let effectiveTrackH = TRACK_HEIGHT;
  if (multiLane) {
    const gap = laneCount > 8 ? 0 : laneCount > 4 ? 1 : 3;
    const neededH = 8 + laneCount * MIN_LANE_H + (laneCount - 1) * gap;
    effectiveTrackH = Math.max(TRACK_HEIGHT, neededH);
  }

  return (
    <div
      style={{
        position: 'relative',
        height: `${effectiveTrackH}px`,
        marginBottom: `${TRACK_GAP}px`,
        backgroundColor: isOffline ? 'var(--gray-100, #f5f5f4)' : 'var(--surface-sunken, #fafaf9)',
        borderRadius: '6px',
        border: '1px solid var(--border-subtle, #ececea)',
        opacity: isOffline ? 0.55 : 1,
      }}
    >
      {drawables.map((d, i) => {
        if (d.kind === 'chip') {
          const dimmed = !!selectedContentId
            && !d.slots.some((ms) => ms.slot.contentId === selectedContentId);
          const highlighted = !!selectedContentId
            && d.slots.some((ms) => ms.slot.contentId === selectedContentId);
          return (
            <AggregationChip
              key={`chip-${i}-${d.leftPct.toFixed(4)}`}
              slots={d.slots.map((ms) => ms.slot)}
              contentMap={contentMap}
              leftPct={d.leftPct}
              widthPct={d.widthPct}
              dimmed={dimmed}
              highlighted={highlighted}
            />
          );
        }

        const { slot, startMin, endMin, leftPct, widthPct, clipLeft, clipRight, px } = d;
        const { label } = resolveSlotLabel(slot, contentMap);
        const color = hashColor(slot.contentId, label);
        const isActive = nowMin >= startMin && nowMin < endMin;
        const dimmed = selectedContentId && slot.contentId !== selectedContentId;
        const highlighted = selectedContentId && slot.contentId === selectedContentId;
        const hasLoop = slot.loopEnabled && Array.isArray(slot.loopItems) && slot.loopItems.length > 1;
        const slotKey = slot.scheduleId || slot.id || `${slot.contentId}-${slot.startTime}`;
        const isHovered = hoveredSlotId === slotKey;
        const isOpen =
          (openSlotId && (slot.scheduleId === openSlotId || slot.id === openSlotId))
          || (selectedIntentId && slot.id === selectedIntentId);

        return (
          <div
            key={slotKey}
            role="button"
            tabIndex={0}
            data-clip-id={slot.id || undefined}
            onClick={(e) => {
              e.stopPropagation();
              // In multi-day mode, click selects the intent for the floating
              // inspector. In day mode, click opens the loop drawer.
              if (onSelectIntent) {
                onSelectIntent(slot);
              } else if (onOpenLoopDrawer) {
                onOpenLoopDrawer(slot);
              }
            }}
            onMouseEnter={() => setHoveredSlotId(slotKey)}
            onMouseMove={(e) => onHoverSlot && onHoverSlot(slot, e.clientX, e.clientY, label)}
            onMouseLeave={() => { setHoveredSlotId(null); onLeaveSlot && onLeaveSlot(); }}
            style={{
              // Day view: 3px inset per side, time-of-day-accurate. Week/Month:
              // every day-pulse renders as a centered pill of fixed
              // PILL_FRACTION × day-column-width, so width is uniform across
              // days regardless of the underlying schedule's time-range
              // (which can differ today vs future days due to mid-day
              // schedule edits, partial-day intents, etc.).
              position: 'absolute',
              ...(suppressAggregation && Number.isFinite(slot._dayIdx) && win.windowDays
                ? (() => {
                    const dayWidthPct = 1 / win.windowDays;
                    const pillLeft = slot._dayIdx * dayWidthPct
                      + dayWidthPct * (1 - PILL_FRACTION) / 2;
                    const pillWidth = dayWidthPct * PILL_FRACTION;
                    return {
                      left: `${pillLeft * 100}%`,
                      width: `${pillWidth * 100}%`,
                    };
                  })()
                : {
                    left: `calc(${leftPct * 100}% + 3px)`,
                    width: `calc(${widthPct * 100}% - 6px)`,
                  }),
              minWidth: `${suppressAggregation ? 6 : MIN_RENDER_PX}px`,
              ...(multiLane
                ? (() => {
                    // Track height grew to fit all lanes at MIN_LANE_H each.
                    const lane = d.subLane ?? 0;
                    const gap = laneCount > 8 ? 0 : laneCount > 4 ? 1 : 3;
                    const laneHPx = Math.max(MIN_LANE_H, (effectiveTrackH - 8 - (laneCount - 1) * gap) / laneCount);
                    return {
                      top: `${4 + lane * (laneHPx + gap)}px`,
                      height: `${laneHPx}px`,
                      bottom: 'auto',
                    };
                  })()
                : { top: '4px', bottom: '4px' }),
              backgroundColor: color.bg,
              color: color.text,
              borderTopLeftRadius: clipLeft ? 0 : '4px',
              borderBottomLeftRadius: clipLeft ? 0 : '4px',
              borderTopRightRadius: clipRight ? 0 : '4px',
              borderBottomRightRadius: clipRight ? 0 : '4px',
              overflow: 'hidden',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              padding: '0 6px',
              opacity: dimmed ? 0.5 : 1,
              // Priority: open-in-drawer > now-airing (Day view only) > hover
              // > highlighted > none. In multi-day view we suppress the
              // now-airing outline because the red Now line already marks
              // current time and the outline made today's pills read as
              // visually different from future days.
              outline: isOpen
                ? '2px solid #0f172a'
                : isHovered
                  ? '2px solid rgba(15,23,42,0.55)'
                  : (isActive && !suppressAggregation)
                    ? '2px solid rgba(255,255,255,0.9)'
                    : highlighted
                      ? '2px solid #fff'
                      : 'none',
              outlineOffset: isOpen || isHovered ? '1px' : 0,
              boxShadow: isOpen
                ? '0 0 0 3px rgba(15,23,42,0.18), 0 2px 6px rgba(17,24,39,0.22)'
                : (isActive && !highlighted && !suppressAggregation)
                  ? `0 0 0 2px ${color.bg}, 0 0 0 3px rgba(255,255,255,0.6), 0 1px 2px rgba(17,24,39,0.12)`
                  : '0 1px 2px rgba(17,24,39,0.12)',
              transition: 'opacity 0.15s ease, outline-color 0.12s ease, box-shadow 0.12s ease',
            }}
          >
            {hasLoop && (() => {
              const totalDur = slot.loopItems.reduce((a, x) => a + (x.durationSeconds || 10), 0);
              // Which loop item is currently airing? Compute client-side from
              // wall clock modulo loop duration. Only meaningful while the
              // slot is the active one on this track.
              let activeIdx = -1;
              if (isActive && effectiveNowMs && totalDur > 0) {
                const todayStart = new Date(effectiveNowMs);
                todayStart.setHours(0, 0, 0, 0);
                const slotStartMs = todayStart.getTime() + startMin * 60 * 1000;
                const elapsedSec = Math.max(0, Math.floor((effectiveNowMs - slotStartMs) / 1000));
                let cur = elapsedSec % totalDur;
                for (let i = 0; i < slot.loopItems.length; i += 1) {
                  const d = slot.loopItems[i].durationSeconds || 10;
                  if (cur < d) { activeIdx = i; break; }
                  cur -= d;
                }
              }
              // Two render modes, keyed off item count:
              //  ≤20 items → filmstrip with thumbnails where available
              //  >20      → density microbar (very thin strips, no dividers)
              const lc = slot.loopItems.length;
              const mode = lc <= 20 ? 'filmstrip' : 'density';
              return (
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex',
                }}>
                  {slot.loopItems.map((li, idx) => {
                    const pct = ((li.durationSeconds || 10) / totalDur) * 100;
                    const isNowItem = idx === activeIdx;
                    const itemColor = hashColor(li.contentId, li.contentId || '').bg;
                    const asset = resolveSlotAsset({ contentId: li.contentId }, contentMap);
                    const itemMediaType = li?.mediaType || asset?.mediaType || null;
                    const itemIsVideo = itemMediaType && String(itemMediaType).toLowerCase().startsWith('video');
                    // Backend dumps the raw .mp4 URL into thumbnailUrl for video
                    // assets — rendering that as a CSS backgroundImage silently
                    // fails. Only treat thumbnailUrl as an image when mediaType
                    // is not video.
                    const showThumb = mode === 'filmstrip' && asset?.thumbnailUrl && !itemIsVideo;
                    const itemSelected = selectedContentId && li.contentId === selectedContentId;
                    return (
                      <div
                        key={idx}
                        style={{
                          width: `${pct}%`,
                          backgroundColor: itemColor,
                          backgroundImage: showThumb ? `url(${asset.thumbnailUrl})` : 'none',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          borderRight: mode === 'density'
                            ? 'none'
                            : (idx < slot.loopItems.length - 1
                              ? '1px solid rgba(255,255,255,0.55)' : 'none'),
                          boxShadow: isNowItem
                            ? 'inset 0 -2px 0 0 rgba(255,255,255,0.95), inset 0 0 0 9999px rgba(255,255,255,0.18)'
                            : itemSelected
                              ? 'inset 0 0 0 2px #fff'
                              : (showThumb ? 'inset 0 0 0 9999px rgba(17,24,39,0.18)' : 'none'),
                          // Subdivisions are visual-only — the whole slot bar
                          // is a single click target that opens the drawer.
                          pointerEvents: 'none',
                          transition: 'box-shadow 0.2s ease',
                        }}
                      />
                    );
                  })}
                </div>
              );
            })()}
            {!hasLoop && (() => {
              const asset = resolveSlotAsset(slot, contentMap);
              const isVideo = asset?.mediaType && String(asset.mediaType).toLowerCase().startsWith('video');
              // Backend dumps the raw .mp4 URL into thumbnailUrl for video
              // assets — only treat thumbnailUrl as an image when mediaType
              // is not video.
              if (asset?.thumbnailUrl && !isVideo) {
                return (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: `url(${asset.thumbnailUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      boxShadow: 'inset 0 0 0 9999px rgba(17,24,39,0.18)',
                      pointerEvents: 'none',
                    }}
                  />
                );
              }
              if (isVideo) {
                return (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      color: '#fff',
                      fontSize: '12px',
                      fontWeight: 600,
                      textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                      pointerEvents: 'none',
                      padding: '0 8px',
                      overflow: 'hidden',
                    }}
                  >
                    <span style={{ fontSize: '14px', flexShrink: 0 }}>▶</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {label}
                    </span>
                  </div>
                );
              }
              return null;
            })()}
            {/* Text + glyph removed: identity lives in the hover tooltip and
                the right-rail gallery. The bar is a pure color/thumb channel. */}
          </div>
        );
      })}
    </div>
  );
};

export default TimelineTrack;
